"use client";

import { IconBook2, IconChevronRight, IconLayoutDashboard } from "@tabler/icons-react";
import clsx from "clsx";
import { Children, type ReactNode } from "react";
import Link, { type LinkProps } from "src/components/common/Link/Link";
import { useExtracted } from "#i18n";
import styles from "./BreadcrumbLink.module.scss";

export type BreadcrumbLinkProps = Omit<LinkProps, "variant"> & { icon?: ReactNode };

/** An inline page link. Text written as `Configuration → Metadata` becomes a navigation trail. */
export default function BreadcrumbLink({
  children,
  className,
  icon,
  ...props
}: BreadcrumbLinkProps) {
  const translateMessage = useExtracted();
  const docsLabel = translateMessage("Docs");
  const destination = props.href;
  const portalLink = destination === "/portal" || destination?.startsWith("/portal/");
  const portalLabel = translateMessage("Portal");
  const rootLabel = portalLink ? portalLabel : docsLabel;
  const referenceIcon =
    icon ?? (portalLink ? <IconLayoutDashboard aria-hidden /> : <IconBook2 aria-hidden />);
  if ((destination === "/docs" || destination?.startsWith("/docs/")) && icon === undefined) {
    return (
      <Link {...props} variant="bare" className={clsx(styles.link, className)}>
        {children}
      </Link>
    );
  }
  const parts = Children.toArray(children);
  const text = parts.every((part) => typeof part === "string" || typeof part === "number")
    ? parts.join("")
    : "";
  // Labels are shown as written (translated text must not be re-cased).
  const trail = text.split("→").map((part) => part.trim());
  const prefixDocs =
    destination?.startsWith("/docs/") &&
    ![docsLabel.toLowerCase(), "docs", "documentation"].includes(trail[0].toLowerCase());
  const prefixPortal =
    portalLink &&
    icon === undefined &&
    ![portalLabel.toLowerCase(), "portal"].includes(trail[0].toLowerCase());
  const prefixRoot = prefixDocs || prefixPortal;
  if (prefixRoot && text) trail.unshift(rootLabel);
  const hasTrail = trail.length > 1 && trail.every(Boolean);
  return (
    <Link
      {...props}
      variant="bare"
      data-page-link-breadcrumb=""
      className={clsx(styles.link, className)}
    >
      <span className={styles.icon} data-reference-icon="" aria-hidden="true">
        {referenceIcon}
      </span>
      {hasTrail ? (
        trail.map((label, index) => (
          <span key={`${index}-${label}`} className={styles.step}>
            {index > 0 && (
              <>
                <span className="sr-only"> › </span>
                <IconChevronRight size={12} aria-hidden />
              </>
            )}
            {label}
          </span>
        ))
      ) : prefixRoot ? (
        <>
          <span className={styles.step}>{rootLabel}</span>
          <span className={styles.step}>
            <span className="sr-only"> › </span>
            <IconChevronRight size={12} aria-hidden />
            {children}
          </span>
        </>
      ) : (
        children
      )}
    </Link>
  );
}
